type UTMParams = Record<string, string>;

const UTM_COOKIE_NAME = 'utm_params';
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 90; // 90 days

const parseJson = (value: string) => {
    try {
        return JSON.parse(value) as UTMParams;
    } catch (error) {
        console.error('Failed to parse UTM cookie', error);
        return null;
    }
};

const serializeCookie = (utmParams: UTMParams) => {
    const encoded = encodeURIComponent(JSON.stringify(utmParams));
    return `${UTM_COOKIE_NAME}=${encoded}; path=/; max-age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax`;
};

export const parseUtmParamsFromSearch = (search: string): UTMParams => {
    const params = new URLSearchParams(search);
    const utmEntries: [string, string][] = [];

    params.forEach((value, key) => {
        if (key.toLowerCase().startsWith('utm_') && value) {
            utmEntries.push([key.toLowerCase(), value]);
        }
    });

    return Object.fromEntries(utmEntries);
};

export const getStoredUtmParams = (): UTMParams | null => {
    if (typeof document === 'undefined') return null;

    const cookies = document.cookie.split('; ');
    const utmCookie = cookies.find(cookie => cookie.startsWith(`${UTM_COOKIE_NAME}=`));

    if (utmCookie) {
        const value = utmCookie.split('=').slice(1).join('=');
        return parseJson(decodeURIComponent(value));
    }

    // Fallback: read UTM keys written to localStorage by syncUrlParamsToSessionStorage
    const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
    const fromStorage: UTMParams = {};
    UTM_KEYS.forEach(key => {
        const val = localStorage.getItem(key);
        if (val) fromStorage[key] = val;
    });
    return Object.keys(fromStorage).length > 0 ? fromStorage : null;
};

export const persistUtmParams = (utmParams: UTMParams) => {
    if (typeof document === 'undefined' || !Object.keys(utmParams).length) return;

    document.cookie = serializeCookie(utmParams);
};

export const upsertUtmCookieFromSearch = (search: string): UTMParams | null => {
    const utmParams = parseUtmParamsFromSearch(search);
    if (typeof document === 'undefined' || !Object.keys(utmParams).length) return null;

    persistUtmParams(utmParams);
    return utmParams;
};
